import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Image as ImageIcon, MapPin, Clock, User, Trash2, DollarSign, Receipt, Pencil } from "lucide-react";

const priorityClass = { high: "prio-high", medium: "prio-medium", low: "prio-low" };
const statusLabel = { open: "Open", in_progress: "In Progress", completed: "Completed" };

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function IssueCard({ issue, isAdmin, currentUserId, onStatusChange, onOpenPhoto, onDelete, onEditCost, onEdit }) {
  const statusCls = `status-${issue.status}`;
  const prioCls = priorityClass[issue.priority] || "prio-low";
  const hasCost = issue.total_cost != null && issue.total_cost > 0;
  const receiptCount = (issue.receipts || []).length;
  const isReporter = currentUserId && issue.reporter_id === currentUserId;
  const canEdit = !!onEdit && (isAdmin || (isReporter && issue.status !== "completed"));

  return (
    <div
      data-testid={`issue-card-${issue.id}`}
      className={`bg-white border rounded-lg p-5 transition-shadow hover:shadow-md ${
        issue.status === "completed" ? "opacity-70" : ""
      }`}
      style={{ borderColor: "#E4E4E7" }}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox-style completion control */}
        <button
          data-testid={`issue-toggle-${issue.id}`}
          disabled={!isAdmin}
          onClick={() =>
            onStatusChange(issue, issue.status === "completed" ? "open" : "completed")
          }
          className={`mt-1 shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
            issue.status === "completed"
              ? "bg-[#059669] border-[#059669] text-white"
              : "border-gray-300 hover:border-[#0055FF]"
          } ${!isAdmin ? "cursor-default" : "cursor-pointer"}`}
          aria-label="Toggle completed"
        >
          {issue.status === "completed" && <Check size={14} strokeWidth={3} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start gap-2 mb-2">
            <h3
              className={`font-display font-semibold text-base sm:text-lg tracking-tight ${
                issue.status === "completed" ? "line-through text-gray-500" : "text-gray-900"
              }`}
            >
              {issue.title}
            </h3>
            <span className={`pill ${prioCls}`} data-testid={`issue-priority-${issue.id}`}>
              {issue.priority}
            </span>
            <span className={`pill ${statusCls}`} data-testid={`issue-status-${issue.id}`}>
              {statusLabel[issue.status]}
            </span>
            {canEdit && (
              <button
                type="button"
                data-testid={`issue-edit-${issue.id}`}
                onClick={() => onEdit(issue)}
                aria-label="Edit issue"
                title="Edit title, description and priority"
                className="ml-auto inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-[#0055FF] hover:bg-blue-50 transition-colors"
              >
                <Pencil size={14} />
              </button>
            )}
          </div>

          {issue.description && (
            <p className="text-sm text-gray-600 mb-3 whitespace-pre-wrap">{issue.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <MapPin size={12} /> {issue.location_name || "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <User size={12} /> {issue.reporter_name || "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {timeAgo(issue.created_at)}
            </span>
            {issue.completed_at && (
              <span className="inline-flex items-center gap-1 text-[#059669]">
                <Check size={12} /> Completed {timeAgo(issue.completed_at)}
              </span>
            )}
            {hasCost && (
              <span className="inline-flex items-center gap-1 text-gray-700 font-semibold tabular-nums" data-testid={`issue-total-${issue.id}`}>
                <DollarSign size={12} /> {fmtMoney(issue.total_cost)}
              </span>
            )}
            {issue.vendor_name && (
              <span className="inline-flex items-center gap-1 text-gray-600">
                · {issue.vendor_name}
              </span>
            )}
            {receiptCount > 0 && (
              <span className="inline-flex items-center gap-1 text-gray-500">
                <Receipt size={12} /> {receiptCount} receipt{receiptCount === 1 ? "" : "s"}
              </span>
            )}
          </div>

          {Array.isArray(issue.photos) && issue.photos.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`issue-photos-${issue.id}`}>
              {issue.photos.map((src, idx) => (
                <button
                  key={`${issue.id}-photo-${idx}-${(src || "").slice(-24)}`}
                  type="button"
                  data-testid={`issue-photo-${issue.id}-${idx}`}
                  onClick={() => onOpenPhoto?.(src)}
                  aria-label={`Open photo ${idx + 1}`}
                  className="block group relative overflow-hidden rounded-md border border-gray-200 hover:border-[#0055FF] transition-colors"
                >
                  <img
                    src={src}
                    alt={`Issue attachment ${idx + 1}`}
                    className="h-20 w-20 sm:h-24 sm:w-24 object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <ImageIcon size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </button>
              ))}
            </div>
          ) : issue.photo ? (
            <button
              type="button"
              data-testid={`issue-photo-${issue.id}`}
              onClick={() => onOpenPhoto?.(issue.photo)}
              aria-label="Open photo"
              className="mt-3 block group relative overflow-hidden rounded-md border border-gray-200 hover:border-[#0055FF] transition-colors"
            >
              <img
                src={issue.photo}
                alt="Issue attachment"
                className="h-20 w-20 sm:h-24 sm:w-24 object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ImageIcon size={16} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </button>
          ) : null}

          {isAdmin && issue.status !== "completed" && (
            <div className="mt-4 flex flex-wrap gap-2">
              {issue.status === "open" && (
                <Button
                  data-testid={`issue-start-${issue.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onStatusChange(issue, "in_progress")}
                >
                  Mark in progress
                </Button>
              )}
              <Button
                data-testid={`issue-complete-${issue.id}`}
                size="sm"
                className="bg-[#0055FF] hover:bg-[#0044CC]"
                onClick={() => onStatusChange(issue, "completed")}
              >
                <Check size={14} className="mr-1" /> Mark complete
              </Button>
              {onEditCost && (
                <Button
                  data-testid={`issue-cost-${issue.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onEditCost(issue)}
                >
                  <DollarSign size={14} className="mr-1" />
                  {hasCost ? "Edit cost" : "Add cost"}
                </Button>
              )}
              {onDelete && (
                <Button
                  data-testid={`issue-delete-${issue.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(issue)}
                  className="text-gray-400 hover:text-red-600 hover:bg-red-50 ml-auto"
                  aria-label="Delete issue"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          )}
          {isAdmin && issue.status === "completed" && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                data-testid={`issue-reopen-${issue.id}`}
                size="sm"
                variant="outline"
                onClick={() => onStatusChange(issue, "open")}
              >
                Reopen
              </Button>
              {onEditCost && (
                <Button
                  data-testid={`issue-cost-${issue.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => onEditCost(issue)}
                >
                  <DollarSign size={14} className="mr-1" />
                  {hasCost ? "Edit cost" : "Add cost"}
                </Button>
              )}
              {onDelete && (
                <Button
                  data-testid={`issue-delete-${issue.id}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => onDelete(issue)}
                  className="text-gray-400 hover:text-red-600 hover:bg-red-50 ml-auto"
                  aria-label="Delete issue"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
